import ProfileIcon from '@assets/profile.png'
import { DocumentList } from '@components/chat/DocumentList'
import { Document, RetrievedImage } from '@components/chat/types'
import { keyframes } from '@emotion/react'
import Circle from '@mui/icons-material/Circle'
import ErrorIcon from '@mui/icons-material/Error'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import { grey } from '@mui/material/colors'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Zoom from '@mui/material/Zoom'
import { useTranslation } from 'react-i18next'
import { Markdown } from './Markdown'

const typing = keyframes`
  0% {
    color: ${grey['600']};
  }
  50% {
    color: ${grey['800']};
  }
  100% {
    color: ${grey['600']};
  }
`

type BaseProps = {
  position: 'left' | 'right'
}

type MessageProps = {
  createdAt?: string
  message: string
  typing?: boolean
  documents?: Document[]
  images?: RetrievedImage[]
  empty?: boolean
  error?: boolean
}

type Props = BaseProps & MessageProps

export const Message = (props: Props) => {
  const { t } = useTranslation()
  const isLeft = props.position === 'left'

  return (
    <Zoom in>
      <Box
        display="flex"
        alignItems="center"
        justifyContent={isLeft ? 'flex-start' : 'flex-end'}
      >
        <Paper
          sx={{
            py: 2,
            pl: !props?.empty && isLeft ? 8 : 4,
            pr: isLeft ? 4 : 8,
            mx: 2,
            maxWidth: '75%',
            background: (theme) => theme.palette.background.paper,
            borderTopLeftRadius: !props?.empty && isLeft ? 0 : undefined,
            borderTopRightRadius: isLeft ? undefined : 0,
            borderBottom: (theme) => `3px solid ${theme.palette.primary.main}`,
            boxShadow: (theme) => theme.shadows[3],
          }}
        >
          <Box
            display="grid"
            gap={2}
            position="relative"
          >
            {props.error ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  backgroundColor: (theme) => theme.palette.error.light + '20',
                  border: (theme) => `1px solid ${theme.palette.error.main}`,
                  borderRadius: 1,
                  order: 1,
                }}
              >
                <ErrorIcon
                  sx={{
                    color: (theme) => theme.palette.error.main,
                    fontSize: '1.2rem',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.error.dark,
                    fontWeight: 500,
                  }}
                >
                  {t('conversations.message_error')}
                </Typography>
              </Box>
            ) : undefined}
            {props?.empty ? (
              <Box
                sx={{
                  order: 2,
                  display: 'grid',
                  justifyItems: 'center',
                  gridTemplateColumns: 'auto 1fr',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <img
                  src={ProfileIcon}
                  alt="Profile"
                  width={'120px'}
                />
                {props?.message}
              </Box>
            ) : (
              <>
                <Avatar
                  sx={(theme) => ({
                    position: 'absolute',
                    top: -36,
                    left: isLeft ? -56 : undefined,
                    right: isLeft ? undefined : -56,
                    order: isLeft ? 1 : 3,
                    background: isLeft ? theme.palette.primary.main : theme.palette.background.paper,
                    border: `1px solid ${theme.palette.primary.main}`,
                    color: isLeft ? theme.palette.primary.contrastText : theme.palette.primary.main,
                    boxShadow: theme.shadows[8],
                  })}
                >
                  {isLeft ? (
                    <img
                      src={ProfileIcon}
                      alt="M"
                      width={40}
                    />
                  ) : null}
                </Avatar>
                <Box
                  sx={{ order: 2, overflowWrap: 'anywhere' }}
                  className="markdown-body"
                >
                  <Markdown
                    message={props.message}
                    images={props.images}
                  />
                </Box>
              </>
            )}
            {props?.documents && !props?.typing && (
              <DocumentList
                documents={props.documents}
                order={5}
              />
            )}
            {props?.typing && (
              <Box
                width="100%"
                order={6}
              >
                <LoadingMessage />
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Zoom>
  )
}

const LoadingMessage = () => (
  <Box
    sx={{
      display: 'flex',
      gap: 0.5,
      alignItems: 'center',
      '& > *': {
        fontSize: '0.75rem',
        animation: `${typing} 1s infinite`,
      },
      '& > *:nth-of-type(2)': {
        animationDelay: '0.3s',
      },
      '& > *:nth-of-type(3)': {
        animationDelay: '0.6s',
      },
    }}
  >
    <Circle />
    <Circle />
    <Circle />
  </Box>
)
